/**
 * Shopify Storefront API client
 * --------------------------------
 * Uses the PUBLIC Storefront API access token (safe to expose in frontend).
 * Talks directly to Shopify GraphQL — no backend, no syncing.
 * Product changes in Shopify Admin appear on the next page load.
 */

const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const API_VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION || "2024-10";

export const SHOPIFY_CONFIGURED = Boolean(DOMAIN && TOKEN);

const ENDPOINT = SHOPIFY_CONFIGURED
    ? `https://${DOMAIN}/api/${API_VERSION}/graphql.json`
    : null;

async function shopifyFetch(query, variables = {}) {
    if (!SHOPIFY_CONFIGURED) {
        throw new Error("Shopify is not configured");
    }
    const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": TOKEN,
            Accept: "application/json",
        },
        body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) throw new Error(`Shopify HTTP ${res.status}`);
    const json = await res.json();
    if (json.errors?.length) {
        throw new Error(json.errors.map((e) => e.message).join("; "));
    }
    return json.data;
}

const PRODUCT_FIELDS = `
    id
    title
    handle
    description
    onlineStoreUrl
    tags
    availableForSale
    featuredImage { url altText width height }
    images(first: 4) { edges { node { url altText } } }
    priceRange {
        minVariantPrice { amount currencyCode }
        maxVariantPrice { amount currencyCode }
    }
    compareAtPriceRange {
        minVariantPrice { amount currencyCode }
    }
    variants(first: 1) {
        edges { node { id availableForSale } }
    }
`;

/** Fetch up to N products from the store (newest first). */
export async function fetchAllProducts(limit = 24) {
    const query = `
        query Products($limit: Int!) {
            products(first: $limit, sortKey: CREATED_AT, reverse: true) {
                edges { node { ${PRODUCT_FIELDS} } }
            }
        }
    `;
    const data = await shopifyFetch(query, { limit });
    return data.products.edges.map((e) => normalizeProduct(e.node));
}

/** Fetch products inside a specific collection handle (e.g. "jewelry"). */
export async function fetchCollectionProducts(handle, limit = 24) {
    const query = `
        query Collection($handle: String!, $limit: Int!) {
            collection(handle: $handle) {
                id
                title
                handle
                description
                image { url altText }
                products(first: $limit) {
                    edges { node { ${PRODUCT_FIELDS} } }
                }
            }
        }
    `;
    const data = await shopifyFetch(query, { handle, limit });
    if (!data.collection) return null;
    return {
        id: data.collection.id,
        title: data.collection.title,
        handle: data.collection.handle,
        description: data.collection.description,
        image: data.collection.image,
        products: data.collection.products.edges.map((e) =>
            normalizeProduct(e.node)
        ),
    };
}

/** Fetch all collections so we can show categories that actually exist. */
export async function fetchCollections(limit = 20) {
    const query = `
        query Collections($limit: Int!) {
            collections(first: $limit) {
                edges {
                    node {
                        id
                        title
                        handle
                        description
                        image { url altText }
                    }
                }
            }
        }
    `;
    const data = await shopifyFetch(query, { limit });
    return data.collections.edges.map((e) => e.node);
}

/** Create a cart with one variant and return the checkout URL. */
export async function createCheckoutUrl(variantId, quantity = 1) {
    const query = `
        mutation CartCreate($lines: [CartLineInput!]!) {
            cartCreate(input: { lines: $lines }) {
                cart { id checkoutUrl }
                userErrors { field message }
            }
        }
    `;
    const data = await shopifyFetch(query, {
        lines: [{ merchandiseId: variantId, quantity }],
    });
    if (data.cartCreate.userErrors?.length) {
        throw new Error(data.cartCreate.userErrors[0].message);
    }
    return data.cartCreate.cart.checkoutUrl;
}

function normalizeProduct(p) {
    const price = p.priceRange?.minVariantPrice;
    const compareAt = p.compareAtPriceRange?.minVariantPrice;
    return {
        id: p.id,
        handle: p.handle,
        title: p.title,
        description: p.description,
        tags: p.tags || [],
        available: p.availableForSale,
        image:
            p.featuredImage ||
            p.images?.edges?.[0]?.node ||
            null,
        images: p.images?.edges?.map((e) => e.node) || [],
        variantId: p.variants?.edges?.[0]?.node?.id || null,
        price: price ? { amount: parseFloat(price.amount), currency: price.currencyCode } : null,
        compareAtPrice:
            compareAt && parseFloat(compareAt.amount) > 0
                ? { amount: parseFloat(compareAt.amount), currency: compareAt.currencyCode }
                : null,
        productUrl: p.onlineStoreUrl ||
            `https://${DOMAIN}/products/${p.handle}`,
    };
}

export function formatPrice(price) {
    if (!price) return "";
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: price.currency || "USD",
        }).format(price.amount);
    } catch {
        return `$${price.amount.toFixed(2)}`;
    }
}
